import { DOCUMENT } from '@angular/core';
import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FocusModalityService } from '@core/services/focus-modality/focus-modality.service';
import { ModalAccessibilityService } from '@core/services/modal-accessibility/modal-accessibility.service';
import { NgSelectAccessibilityService } from '@core/services/ng-select-accessibility/ng-select-accessibility.service';
import { RouteFocusService } from '@core/services/route-focus/route-focus.service';
import { ChatbotService } from '@core/services/chatbot/chatbot.service';
import { ErrorHandlerService } from '@core/services/errors/error-handler.service';
import { RemoteConfigService } from '@core/services/firebase/remote-config.service';
import { MosaicService } from '@core/services/mosaic/mosaic.service';
import { OneTrustCokkiesService } from '@core/services/oneTrust-cookies/oneTrust-cookies.service';
import { environment } from '@environment/environment';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { setTheme } from 'ngx-bootstrap/utils';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { MosaicModalService } from './mosaic/services/mosaic-modal.service';
import { InactivityService } from '@core/services/inactivity/inactivity.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styles: [''],
  standalone: false,
})
export class AppComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('cookiesModal') cookiesModal?: TemplateRef<any>;
  private modalRef?: BsModalRef;
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private errorHandler: ErrorHandlerService,
    private bsModalService: BsModalService,
    private gtmService: GoogleTagManagerService,
    private cookieService: CookieService,
    private chatbotService: ChatbotService,
    private oneTrustCokkiesService: OneTrustCokkiesService,
    private mosaicService: MosaicService,
    private remoteConfigService: RemoteConfigService,

    private inactivityService: InactivityService,
    private focusModalityService: FocusModalityService,
    private modalAccessibilityService: ModalAccessibilityService,
    private routeFocusService: RouteFocusService,
    private ngSelectAccessibilityService: NgSelectAccessibilityService,
  ) {
    setTheme('bs4');
    this.handlePageChangeEvents();
    this.setModalSettings();
    //Se asegura de que las variables de OneTrust estén definidas antes de cargar GTM y el script de cookies.
    const win = window as any;
    if (!win.OneTrust) {
      win.OneTrust = {
        OnConsentChanged: () => {},
      };
    }

    if (typeof win.OnetrustActiveGroups === 'undefined') {
      win.OnetrustActiveGroups = '';
    }
    this.gtmService.addGtmToDom();
    this.oneTrustCokkiesService.loadScript({ id: 'cookiesScript', key: environment.oneTrustKey });
    this.oneTrustCokkiesService.optanonWrapper({ id: 'cookiesScript' });
  }

  ngOnInit(): void {
    this.focusModalityService.init();
    this.modalAccessibilityService.init();
    this.routeFocusService.init();
    this.ngSelectAccessibilityService.init();
    this.chatbotService
      .loadScript()
      .catch((err) => console.error('Fallo al cargar el chatbot', err));

    this.initializeShowMosaicFlag();
    this.initializeMosaicSDK();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.focusModalityService.destroy();
    this.modalAccessibilityService.destroy();
    this.routeFocusService.destroy();
    this.ngSelectAccessibilityService.destroy();
    this.chatbotService.removeScript();
    this.mosaicService.removeScriptMosaic();
  }

  ngAfterViewInit() {
    // this.checkCookies();
  }

  private handlePageChangeEvents() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        window.scrollTo(0, 0);
        this.errorHandler.hide();
      });
  }

  private setModalSettings() {
    this.bsModalService.config.backdrop = 'static';
    this.bsModalService.config.keyboard = false;
    this.bsModalService.config.animated = false;
    this.bsModalService.config.class = 'modal-dialog-centered';
  }

  private async initializeShowMosaicFlag() {
    try {
      await this.remoteConfigService.initializeRemoteConfig();
    } catch (error) {
      console.error('Error al inicializar el flag de Remote Config:', error);
    }
  }

  private initializeMosaicSDK() {
    this.remoteConfigService.isTransmitEnabled$
      .pipe(
        filter((enabled) => enabled),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.mosaicService.loadSDkMosaic().catch((err) => {
          console.error('Error en ngOnInit', err);
        });
      });
  }
}
